import React from 'react';

import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { isNil } from 'lodash';

import { Avatar } from 'uiToolkitV1/Avatar';
import { useExpandableContainer } from 'uiToolkitV1/ExpandableContainerProvider';
import { TextLabel } from 'uiToolkitV1/Typography/Typography';

import CalibrationScatterPlot from '../../../components/CalibrationScatterPlot';
import PerformanceGridLayout from '../../../components/PerformanceGridLayout';

function ReviewManagePerformanceGrid({
  performanceGridData,
  performanceGridEmpDetails,
  performanceGridNoRating,
  isLoading,
  showTooltip = true,
  isDisplayPictures = true,
  isSingleEmpVersion = false,
}) {
  const rows = performanceGridData?.length || 0;
  const columns = performanceGridData?.[0]?.length || 0;

  const isHorizontal1DGraph = rows === 1;

  // if 1d grid, add a dummy y axis value data point,'dummyValueToCenterVertically' with value 1, to center the avatars vertically,
  // when the graph is 1d, the domain of y axis is set to literal value [0,2]
  let derivedPerformanceGridEmpDetails = !isHorizontal1DGraph
    ? performanceGridEmpDetails
    : (performanceGridEmpDetails ?? []).map(({ managerRating, ...rest }) => ({
        ...rest,
        managerRating: { ...managerRating, dummyValueToCenterVertically: 1 },
      }));

  // if 2d grid but any of goal or competency not available then do not render the employee
  // https://linear.app/peoplemesh/issue/ENG-18904/annual-presets-template-doesnt-have-goals-and-competency-questions
  if (!isHorizontal1DGraph) {
    derivedPerformanceGridEmpDetails = derivedPerformanceGridEmpDetails.filter(
      ({ managerRating }) => !isNil(managerRating?.performance) && !isNil(managerRating?.potential)
    );
  }

  const { isExpanded = false } = useExpandableContainer();
  const boxHeight = isExpanded ? 545 / rows : 255 / rows;

  if (rows === 0 || columns === 0) {
    return null;
  }

  const gridScoreRange = {
    PERFORMANCE: {
      minScore: isHorizontal1DGraph
        ? performanceGridData?.[0]?.[0]?.scoreRange?.OVERALL?.minScore || 0
        : performanceGridData?.[0]?.[0]?.scoreRange?.PERFORMANCE?.minScore || 0,
      maxScore: isHorizontal1DGraph
        ? performanceGridData?.[performanceGridData.length - 1]?.[
            performanceGridData[performanceGridData.length - 1].length - 1
          ]?.scoreRange?.OVERALL?.maxScore || 5
        : performanceGridData?.[performanceGridData.length - 1]?.[
            performanceGridData[performanceGridData.length - 1].length - 1
          ]?.scoreRange?.PERFORMANCE?.maxScore || 5,
    },
    POTENTIAL: {
      minScore: isHorizontal1DGraph ? 0 : performanceGridData?.[0]?.[0]?.scoreRange?.POTENTIAL?.minScore || 0,
      maxScore: isHorizontal1DGraph
        ? 1
        : performanceGridData?.[performanceGridData.length - 1]?.[
            performanceGridData[performanceGridData.length - 1].length - 1
          ]?.scoreRange?.POTENTIAL?.maxScore || 5,
    },
    OVERALL: {
      minScore: isHorizontal1DGraph ? 0 : performanceGridData?.[0]?.[0]?.scoreRange?.OVERALL?.minScore || 0,
      maxScore: isHorizontal1DGraph
        ? 1
        : performanceGridData?.[performanceGridData.length - 1]?.[
            performanceGridData[performanceGridData.length - 1].length - 1
          ]?.scoreRange?.OVERALL?.maxScore || 5,
    },
  };

  const xAxisTicks = [];
  const yAxisTicks = [];

  for (let i = 0; i < performanceGridData?.length; i += 1) {
    if (isHorizontal1DGraph) {
      yAxisTicks.push(performanceGridData?.[i]?.[0]?.scoreRange?.OVERALL?.minScore);
      if (i === performanceGridData.length - 1) {
        yAxisTicks.push(performanceGridData?.[i]?.[0]?.scoreRange?.OVERALL?.maxScore);
      }
    } else {
      yAxisTicks.push(performanceGridData?.[i]?.[0]?.scoreRange?.POTENTIAL?.minScore);
      if (i === performanceGridData.length - 1) {
        yAxisTicks.push(performanceGridData?.[i]?.[0]?.scoreRange?.POTENTIAL?.maxScore);
      }
    }
  }

  for (let j = 0; j < performanceGridData?.[0]?.length; j += 1) {
    if (isHorizontal1DGraph) {
      xAxisTicks.push(performanceGridData?.[0]?.[j]?.scoreRange?.OVERALL?.minScore);
      if (j === performanceGridData[0].length - 1) {
        xAxisTicks.push(performanceGridData?.[0]?.[j]?.scoreRange?.OVERALL?.maxScore);
      }
    } else {
      xAxisTicks.push(performanceGridData?.[0]?.[j]?.scoreRange?.PERFORMANCE?.minScore);
      if (j === performanceGridData[0].length - 1) {
        xAxisTicks.push(performanceGridData?.[0]?.[j]?.scoreRange?.PERFORMANCE?.maxScore);
      }
    }
  }

  const hideScatterPoints = !isSingleEmpVersion && performanceGridData.flat().some(({ count }) => count >= 20);

  return (
    <Skeleton isLoaded={!isLoading} height="255px">
      <Flex>
        <Box height="255px" width="100%">
          <Box position="relative">
            <CalibrationScatterPlot
              height={rows * boxHeight}
              performanceGridEmpDetails={derivedPerformanceGridEmpDetails}
              scoreRange={gridScoreRange}
              rows={rows}
              columns={columns}
              showTooltip={showTooltip}
              xAxisTicks={xAxisTicks}
              yAxisTicks={yAxisTicks}
              hideYAxisLabelTicks={isHorizontal1DGraph}
              renderPointsAsDots={!isDisplayPictures}
              hideScatterPoints={hideScatterPoints}
            />
            <PerformanceGridLayout
              rows={rows}
              columns={columns}
              // performanceGridEmpDetails={performanceGridEmpDetails}
              boxHeight={boxHeight - 52 / rows}
              spacing="1px"
              boxData={performanceGridData.flat()}
              employeeDetails={performanceGridData.flat()}
              position="absolute"
              margin="10px 0 30px 50px"
              width="calc(100% - 50px - 10px)"
              isExpanded={isExpanded}
              isTextCenterAlign={hideScatterPoints || isSingleEmpVersion}
            />
          </Box>
        </Box>
        {isExpanded ? (
          <Box
            bg={performanceGridNoRating.color}
            ml="16px"
            borderRadius="4px"
            p="12px"
            width="100px"
            height={boxHeight * rows - 40}
          >
            <Flex height="100%" direction="column" justifyContent="space-between" alignItems="center">
              <Box>
                <TextLabel fontWeight="bold">No Rating</TextLabel>
                <TextLabel>{performanceGridNoRating?.count} Members</TextLabel>
              </Box>
              <Flex alignItems="flex-start" flexWrap="wrap" maxHeight="490px" overflowY="scroll">
                {(performanceGridNoRating?.employeeDetails ?? []).map((emp) => (
                  <Avatar src={emp.imageUrl} size={16} mr="2px" ml="2px" mb="4px" />
                ))}
              </Flex>
            </Flex>
          </Box>
        ) : null}
      </Flex>
    </Skeleton>
  );
}

export default ReviewManagePerformanceGrid;
